import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { branding } from '@/config/branding';
import { cn } from '@/lib/cn';
import { useTheme } from '@/state/ThemeContext';

import { SpartanWarrior } from './SpartanWarrior';

/**
 * Animación de entrada.
 *
 * Se ve UNA vez por sesión del navegador, no en cada navegación: una entrada
 * que se repite cada vez que abres una página deja de ser un detalle y pasa a
 * ser un peaje. Dura poco más de dos segundos y se puede saltar con un clic,
 * con Escape o con cualquier tecla.
 *
 * Con `prefers-reduced-motion` no se monta siquiera. No es una animación que
 * se pueda "suavizar": lo honesto ahí es no ponerla.
 *
 * Se pinta por encima de todo pero NO bloquea la carga: la aplicación se monta
 * detrás mientras el telón está subido, así que al desaparecer ya está lista.
 */

const SESSION_KEY = 'cp.intro.seen';
/** Lo que tarda la secuencia completa antes de retirarse sola. */
const DURATION_MS = 2600;

export function IntroCurtain() {
  const { t } = useTranslation();
  const { reducedMotion } = useTheme();
  const [state, setState] = useState<'hidden' | 'playing' | 'leaving'>('hidden');

  useEffect(() => {
    if (reducedMotion) return;

    // `?intro` la vuelve a lanzar aunque ya se haya visto. Sirve para
    // enseñarla a alguien sin tener que abrir una ventana privada, y es lo
    // que la hace revisable mientras se ajusta el dibujo.
    const forced = new URLSearchParams(window.location.search).has('intro');

    let seen = true;
    try {
      seen = window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      // Navegador con almacenamiento capado: mejor no enseñarla que fallar.
      return;
    }
    if (seen && !forced) return;

    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* si no se puede recordar, tampoco pasa nada */
    }
    setState('playing');
  }, [reducedMotion]);

  // Se retira sola al terminar, y el estado 'leaving' da tiempo al fundido.
  useEffect(() => {
    if (state !== 'playing') return;
    const timer = window.setTimeout(() => setState('leaving'), DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  useEffect(() => {
    if (state !== 'leaving') return;
    const timer = window.setTimeout(() => setState('hidden'), 640);
    return () => window.clearTimeout(timer);
  }, [state]);

  // Saltar con cualquier tecla: quien ya la ha visto no quiere buscar el botón.
  useEffect(() => {
    if (state !== 'playing') return;
    const skip = () => setState('leaving');
    window.addEventListener('keydown', skip);
    return () => window.removeEventListener('keydown', skip);
  }, [state]);

  if (state === 'hidden') return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden',
        'bg-[rgb(var(--cp-bg))]',
        state === 'leaving' && 'animate-curtain-out',
      )}
      onClick={() => setState('leaving')}
      // Es decoración: el lector de pantalla no tiene nada que hacer aquí, y
      // la aplicación de detrás ya está montada y anunciada.
      aria-hidden="true"
    >
      {/* Polvo de la arena, muy tenue */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_20%_30%,white_1px,transparent_1px),radial-gradient(circle_at_70%_60%,white_1px,transparent_1px)] [background-size:120px_120px,90px_90px]" />

      {/*
        La sacudida envuelve al guerrero, no al telon entero: si se mueve todo
        —incluido el titulo y el boton de omitir— parece un fallo de render en
        vez de un impacto.
      */}
      <div className="relative animate-screen-shake">
        {/* Anillo de choque que sale del golpe */}
        <span className="pointer-events-none absolute left-1/2 top-1/2 h-[46vmin] w-[46vmin] max-h-[420px] max-w-[420px] -translate-x-1/2 -translate-y-1/2 animate-shock-ring rounded-full border border-gold" />

        <SpartanWarrior animated className="h-[52vmin] w-[52vmin] max-h-[460px] max-w-[460px]" />
      </div>

      <div className="mt-8 flex flex-col items-center gap-3 px-6">
        <h1
          className="animate-title-in font-display text-[clamp(1.75rem,7vw,3rem)] font-semibold uppercase text-ink"
          style={{ animationDelay: '1150ms' }}
        >
          {branding.name}
        </h1>

        <span
          className="animate-rule-grow block h-px w-40 bg-gold-gradient"
          style={{ animationDelay: '1400ms' }}
        />

        <p
          className="animate-fade-up text-center text-sm text-ink-muted"
          style={{ animationDelay: '1550ms' }}
        >
          {t('common.tagline')}
        </p>
      </div>

      <button
        type="button"
        className="absolute bottom-8 rounded-full px-4 py-2 text-xs uppercase tracking-epigraphic text-ink-muted transition hover:text-ink"
        onClick={() => setState('leaving')}
      >
        {t('common.skip')}
      </button>
    </div>
  );
}
